import { useState } from "react";
import { getCorpusFeedPage } from "../../../../lib/corpusApi";
import {
  useAsyncInfiniteData,
  type UseAsyncInfiniteDataResult,
} from "../../../../hooks/useAsyncInfiniteData";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import type { FeedStatisticsLookup } from "../../../../components/domain/data-display/FeedGrid";
import { useFeedStatistics } from "./useFeedStatistics";
import type { CorpusFeed } from "@shared/types/corpus/api.ts";

const PAGE_SIZE = 50;

export interface UseFeedListResult {
  /** The feeds as they load, a page at a time, with the state of that loading. */
  list: UseAsyncInfiniteDataResult<CorpusFeed>;
  statistics: FeedStatisticsLookup;
  /** The search text as typed; the list follows it once the typing pauses. */
  query: string;
  setQuery: (v: string) => void;
}

/** The corpus feeds that match the search text, a page at a time as the reader scrolls, with their
 *  statistics alongside. */
export function useFeedList(): UseFeedListResult {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const list = useAsyncInfiniteData(
    (page) => getCorpusFeedPage({ ...page, q: debouncedQuery || undefined }),
    { pageSize: PAGE_SIZE, key: debouncedQuery }
  );
  const statistics = useFeedStatistics(list.items);
  return { list, statistics, query, setQuery };
}
